// frontend/src/utils/admissibility/enforceResponseAdmissibility.js

const RELATIONSHIP_TERMS = [
  'friend',
  'daughter',
  'son',
  'mom',
  'mother',
  'dad',
  'father',
  'sister',
  'brother',
  'wife',
  'husband',
  'girlfriend',
  'boyfriend',
];

function normalizeText(value = '') {
  return String(value).toLowerCase().trim();
}

function normalizeWhitespace(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function escapeRegExp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getEntityRole(entity = null) {
  return entity?.role || entity?.relationship || entity?.relationship_type || null;
}

function getEntityName(entity = null) {
  return entity?.name || entity?.first_name || null;
}

function getSafeRoleLabel(role = '') {
  const normalized = normalizeText(role);

  switch (normalized) {
    case 'mother':
    case 'mom':
      return 'mom';
    case 'father':
    case 'dad':
      return 'dad';
    case 'daughter':
      return 'daughter';
    case 'son':
      return 'son';
    case 'sister':
      return 'sister';
    case 'brother':
      return 'brother';
    case 'grandmother':
    case 'grandma':
      return 'grandmother';
    case 'grandfather':
    case 'grandpa':
      return 'grandfather';
    case 'friend':
      return 'friend';
    default:
      return normalized || 'someone important';
  }
}

function rolesAreCompatible(mentionedRole = '', activeRole = '') {
  const mentioned = getSafeRoleLabel(mentionedRole);
  const active = getSafeRoleLabel(activeRole);

  if (mentioned === active) return true;

  const compatiblePairs = new Set([
    'mom:mother',
    'mother:mom',
    'dad:father',
    'father:dad',
  ]);

  return compatiblePairs.has(`${mentioned}:${active}`);
}

function getRelationshipTermPattern() {
  return RELATIONSHIP_TERMS.join('|');
}

function getActiveEntityAssignmentPatterns(activeEntity = null) {
  const activeName = getEntityName(activeEntity);
  const relationshipTerms = getRelationshipTermPattern();
  const patterns = [];

  if (activeName) {
    const escapedName = escapeRegExp(activeName);

    patterns.push(
      new RegExp(
        `(\\b${escapedName}\\s+(?:is|was|has been|seems to be|appears to be)\\s+(?:one of\\s+)?your\\s+)(${relationshipTerms})\\b()`,
        'gi'
      ),
      new RegExp(
        `(\\b${escapedName}\\s*,\\s*your\\s+)(${relationshipTerms})\\b()`,
        'gi'
      ),
      new RegExp(
        `(\\byour\\s+)(${relationshipTerms})(\\s*,?\\s*${escapedName}\\b)`,
        'gi'
      )
    );
  }

  patterns.push(
    new RegExp(
      `(\\b(?:he|she|they)\\s+(?:is|was|has been|seems to be|appears to be)\\s+(?:one of\\s+)?your\\s+)(${relationshipTerms})\\b()`,
      'gi'
    ),
    new RegExp(
      `(\\bit sounds like\\s+your\\s+)(${relationshipTerms})\\b()`,
      'gi'
    ),
    new RegExp(
      `(\\byou (?:mentioned|were talking about|told me about)\\s+your\\s+)(${relationshipTerms})\\b()`,
      'gi'
    )
  );

  return patterns;
}

function replaceConflictingRelationshipAssignments({
  response = '',
  activeEntity = null,
} = {}) {
  const activeRole = getEntityRole(activeEntity);
  const safeRole = getSafeRoleLabel(activeRole);

  if (!response || !activeRole || !safeRole) {
    return {
      finalReply: response,
      wasModified: false,
      conflictingRelationship: null,
    };
  }

  let finalReply = response;
  let conflictingRelationship = null;

  getActiveEntityAssignmentPatterns(activeEntity).forEach((pattern) => {
    finalReply = finalReply.replace(pattern, (match, prefix, mentionedRole, suffix = '') => {
      if (rolesAreCompatible(mentionedRole, activeRole)) {
        return match;
      }

      conflictingRelationship = conflictingRelationship || normalizeText(mentionedRole);
      return `${prefix}${safeRole}${suffix}`;
    });
  });

  return {
    finalReply,
    wasModified: finalReply !== response,
    conflictingRelationship,
  };
}

function asksWhoWasThisWith(response = '') {
  const lower = normalizeText(response);

  const patterns = [
    'who was this with',
    'who did you share this with',
    'was this with one of your friends',
    'or someone else close to you',
    'who were you talking about',
    'who are you talking about',
  ];

  return patterns.some((phrase) => lower.includes(phrase));
}

function splitIntoSentences(value = '') {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return [];

  const matches = normalized.match(/[^.!?]+[.!?]?/g);
  return (matches || []).map((sentence) => sentence.trim()).filter(Boolean);
}

function removeUngroundedIdentityQuestions({
  response = '',
  activeEntity = null,
} = {}) {
  const sentences = splitIntoSentences(response);
  const preservedSentences = sentences.filter((sentence) => !asksWhoWasThisWith(sentence));

  if (preservedSentences.length === sentences.length) {
    return {
      finalReply: response,
      wasModified: false,
    };
  }

  if (preservedSentences.length) {
    return {
      finalReply: normalizeWhitespace(preservedSentences.join(' ')),
      wasModified: true,
    };
  }

  const activeName = getEntityName(activeEntity);
  const activeRole = getSafeRoleLabel(getEntityRole(activeEntity));

  if (activeName) {
    return {
      finalReply: `You were talking about ${activeName}.`,
      wasModified: true,
    };
  }

  if (activeRole && activeRole !== 'someone important') {
    return {
      finalReply: `You were talking about your ${activeRole}.`,
      wasModified: true,
    };
  }

  return {
    finalReply: response,
    wasModified: false,
  };
}

function enforceResponseAdmissibility({
  response = '',
  activeEntity = null,
  arbitration = null,
  userName = 'Friend',
} = {}) {
  const shouldPreserveContinuity =
    arbitration?.control?.shouldPreservePronounContinuity === true ||
    arbitration?.control?.shouldCarryForwardEntity === true;

  if (!response || !shouldPreserveContinuity || !activeEntity) {
    return {
      finalReply: response,
      wasModified: false,
      reason: 'no_admissibility_action',
    };
  }

  const relationshipResult = replaceConflictingRelationshipAssignments({
    response,
    activeEntity,
  });

  if (relationshipResult.wasModified) {
    return {
      finalReply: relationshipResult.finalReply,
      wasModified: true,
      reason: `conflicting_relationship_assignment:${relationshipResult.conflictingRelationship}`,
    };
  }

  const identityQuestionResult = removeUngroundedIdentityQuestions({
    response,
    activeEntity,
  });

  if (identityQuestionResult.wasModified) {
    return {
      finalReply: identityQuestionResult.finalReply,
      wasModified: true,
      reason: 'who_was_this_with_conflict',
    };
  }
  return {
    finalReply: response,
    wasModified: false,
    reason: 'passed_admissibility',
  };
}
export { enforceResponseAdmissibility };
